import React, { useEffect } from 'react';
import { Button, InputText } from '../ui';
import { Controller, useForm } from 'react-hook-form';
import { InputValidationError } from '../types';
import { useTranslation } from 'react-i18next';
import { MIN_PASSWORD_LENGTH } from '../../helpers/password';

export interface ChangePasswordFormFields {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface ChangePasswordFormProps {
  validation_error?: {
    current_password?: string;
    new_password?: string;
    new_password_confirmation?: string;
  };
  save_handler: (form: ChangePasswordFormFields) => void;
  password_set: boolean;
  clearValidationErrorsAction: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  save_handler,
  validation_error,
  password_set,
  clearValidationErrorsAction,
}) => {
  const { handleSubmit, errors, getValues, control, setValue, clearErrors, watch } = useForm();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    return () => clearValidationErrorsAction();
  }, []);

  let current_password_props: InputValidationError = {};
  let new_password_props: InputValidationError = {};
  let new_password_confirmation_props: InputValidationError = {};

  const isPasswordConfirmationEquals = (passwordConfirmation: string) => {
    return passwordConfirmation === getValues('new_password');
  };

  const frontend_validation_rules = {
    current_password: { required: t('Enter current password') },
    new_password: { required: t('Enter new password'), minLength: MIN_PASSWORD_LENGTH },
    new_password_confirmation: {
      required: t('Enter new password'),
      validate: {
        isEqualsToPassword: isPasswordConfirmationEquals,
      },
    },
  };

  // Frontend Validation
  if (Object.keys(errors).length !== 0) {
    if (errors.current_password) {
      current_password_props = {
        ...current_password_props,
        error: true,
        helperText: errors.current_password.message,
      };
    }
    if (errors.new_password) {
      let message;
      switch (errors.new_password.type) {
        case 'minLength': {
          message = t('The password must be at least {{length}} characters.', {
            length: MIN_PASSWORD_LENGTH.toString(),
          });
          break;
        }
        default: {
          message = errors.new_password.message;
        }
      }
      new_password_props = {
        ...new_password_props,
        error: true,
        helperText: message,
      };
    }
    if (errors.new_password_confirmation) {
      let message;
      switch (errors.new_password_confirmation.type) {
        case 'isEqualsToPassword': {
          message = t('Password does not match');
          break;
        }
        default: {
          message = errors.new_password_confirmation.message;
        }
      }
      new_password_confirmation_props = {
        ...new_password_confirmation_props,
        error: true,
        helperText: message,
      };
    }
  } else {
    // Backend Validation
    if (validation_error) {
      if (validation_error.current_password) {
        current_password_props = {
          ...current_password_props,
          error: true,
          helperText: validation_error.current_password,
        };
      }
      if (validation_error.new_password) {
        new_password_props = {
          ...new_password_props,
          error: true,
          helperText: validation_error.new_password,
        };
      }
      if (validation_error.new_password_confirmation) {
        new_password_confirmation_props = {
          ...new_password_confirmation_props,
          error: true,
          helperText: validation_error.new_password_confirmation,
        };
      }
    }
  }

  return (
    <div>
      <div className="mb-6">
        <p className="text-xl font-medium mb-1">{t('Password')}</p>
        <p className="text-sm">{t('Change your password')}</p>
      </div>
      <form onSubmit={handleSubmit(save_handler)} className="flex flex-col">
        {password_set && (
          <Controller
            name="current_password"
            defaultValue=""
            control={control}
            rules={frontend_validation_rules.current_password}
            render={({ onChange, value }) => (
              <InputText
                type="password"
                label={t('Current password')}
                value={value}
                onChange={onChange}
                className="mb-3 w-64"
                error={current_password_props.error}
                helperText={current_password_props.helperText}
              />
            )}
          />
        )}
        <Controller
          name="new_password"
          defaultValue=""
          control={control}
          rules={frontend_validation_rules.new_password}
          render={({ onChange, value }) => (
            <InputText
              type="password"
              label={t('New password')}
              value={value}
              onChange={onChange}
              className="mb-3 w-64"
              error={new_password_props.error}
              helperText={new_password_props.helperText}
            />
          )}
        />
        <Controller
          name="new_password_confirmation"
          defaultValue=""
          control={control}
          rules={frontend_validation_rules.new_password_confirmation}
          render={({ onChange, value }) => (
            <InputText
              type="password"
              label={t('Retype new password')}
              value={value}
              onChange={onChange}
              className="mb-3 w-64"
              error={new_password_confirmation_props.error}
              helperText={new_password_confirmation_props.helperText}
            />
          )}
        />
        <Button
          type="submit"
          className="w-24 place-self-center"
          disabled={!watch('current_password') || !watch('new_password') || !watch('new_password_confirmation')}
        >
          <span className="px-14 text-sm">{t('Submit')}</span>
        </Button>
      </form>
    </div>
  );
};

export default ChangePasswordForm;
